import type { ResolvedRemovalOrder } from "../types/removal";
import {
  findCatalogRowById,
  resolveTaxableFromSources,
} from "../utils/readResolveTaxable";
import { getResolvedDiscountDisplay } from "./getResolvedDiscountDisplay";

const round2 = (n: number) => Math.round(Number(n) * 100) / 100;

export type RemovalResolveFormikSync = {
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
};

export type ApplyRemovalResolveOptions = {
  requestedDiscountCode?: string;
  existingFeeRows?: unknown[] | null;
  specialFeeCatalog?: unknown[] | null;
};

export type ApplyRemovalResolveResult = {
  feeSubtotal: number;
  removalTax: number;
  feeTax: number;
  grandTotal: number;
  overallTax: number;
  mergedFees: Array<{
    id: string;
    name: string;
    amount: number;
    taxable: boolean;
    tax_percentage: number | null;
    tax_amount: number;
    required: boolean;
  }>;
};

/**
 * Write backend removal resolve response into Formik totals.
 * Uses legacy `totals.installation_*` field names on removal add/edit pages.
 */
export function applyRemovalResolveResponse(
  resolved: ResolvedRemovalOrder,
  formik: RemovalResolveFormikSync,
  fallbackRemovalSubtotal: number,
  options?: ApplyRemovalResolveOptions
): ApplyRemovalResolveResult {
  const feeRows = Array.isArray(resolved.specialfee) ? resolved.specialfee : [];
  const feeSubtotal = feeRows.reduce((s, f) => s + Number(f.specialfee_amount || 0), 0);
  const removalTax = Number(resolved.removal_tax_amount || 0);
  const feeTax = feeRows.reduce((s, f) => s + Number(f.specialfee_tax_amount || 0), 0);
  const overallTax = Number(resolved.over_all_tax_amount ?? removalTax + feeTax);
  const removalSub = Number(resolved.removal_cost ?? fallbackRemovalSubtotal);
  const grandTotal = Number(
    resolved.total_amount ??
      removalSub + feeSubtotal + overallTax - Number(resolved.discount_amount || 0)
  );

  const mergedFees = feeRows.map((f) => {
    const id = String(f.specialfee_id || "");
    const existingRow = findCatalogRowById(options?.existingFeeRows, id, [
      "id",
      "_id",
      "specialfee_id",
    ]);
    const catalogRow = findCatalogRowById(options?.specialFeeCatalog, id, [
      "id",
      "_id",
      "specialfee_id",
    ]);
    return {
      id,
      name: String(f.specialfee_name || ""),
      amount: Number(f.specialfee_amount || 0),
      taxable: resolveTaxableFromSources(
        [existingRow, catalogRow, f as Record<string, unknown>],
        ["taxable", "specialfee_taxable", "is_taxable"],
        true
      ),
      tax_percentage: f.specialfee_tax_percentage ?? null,
      tax_amount: Number(f.specialfee_tax_amount || 0),
      required: f.required === true,
    };
  });

  formik.setFieldValue("totals.installation_subtotal", removalSub, false);
  formik.setFieldValue("totals.installation_tax_amount", removalTax, false);
  formik.setFieldValue("totals.specialfee_subtotal", feeSubtotal, false);
  formik.setFieldValue("totals.specialfee_tax_amount", feeTax, false);
  formik.setFieldValue("totals.grand_total", grandTotal, false);
  formik.setFieldValue("specialfee", mergedFees, false);
  formik.setFieldValue("tax", overallTax, false);
  formik.setFieldValue("tax_status", resolved.tax_status ?? "", false);

  if (resolved.subtotal_amount != null) {
    formik.setFieldValue("subtotal_amount", Number(resolved.subtotal_amount), false);
  }

  const requestedDiscountCode =
    options?.requestedDiscountCode !== undefined
      ? String(options.requestedDiscountCode).trim()
      : undefined;

  if (requestedDiscountCode === "") {
    formik.setFieldValue("discount_code", "", false);
    formik.setFieldValue("discount_amount", 0, false);
  } else {
    const { code: discountCode, amount: discountAmount } = getResolvedDiscountDisplay(
      resolved as Parameters<typeof getResolvedDiscountDisplay>[0],
      requestedDiscountCode
    );
    formik.setFieldValue("discount_amount", round2(discountAmount), false);
    if (discountCode) {
      formik.setFieldValue("discount_code", discountCode, false);
    } else if (requestedDiscountCode) {
      formik.setFieldValue("discount_code", requestedDiscountCode, false);
    }
  }

  const removalRate = resolved.removal_tax_percentage ?? "";
  const overallRate = resolved.over_all_tax_percentage ?? "";
  formik.setFieldValue("calc_install_rate_str", removalRate, false);
  formik.setFieldValue("calc_overall_rate_str", overallRate, false);

  if (resolved.cart_id) {
    formik.setFieldValue("totals.cartId", String(resolved.cart_id), false);
  }

  return {
    feeSubtotal,
    removalTax,
    feeTax,
    grandTotal,
    overallTax,
    mergedFees,
  };
}
